import React, { useState } from 'react';
import {
  ArrowLeft,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  Play,
  Shield,
  Shuffle,
  Users,
} from 'lucide-react';
import { useT } from '../../i18n';

export interface MiniTournamentsViewProps {
  /** Navigates back to the previous screen. */
  onBack: () => void;
  /** Opens the Americano flow. */
  onOpenAmericano: () => void;
  /** Opens the Team Americano flow. */
  onOpenTeamAmericano: () => void;
  /** Opens the Mexicano flow. */
  onOpenMexicano: () => void;
  /** Opens the Mix Mexicano flow. */
  onOpenMixicano: () => void;
  /** Opens the Team Mexicano flow. */
  onOpenTeamMexicano: () => void;
  /** Opens the Mix Americano flow. */
  onOpenMixAmericano: () => void;
  /** Opens the Club Americano flow. */
  onOpenClubAmericano: () => void;
  /** Opens the Club Team Americano flow. */
  onOpenClubTeamAmericano: () => void;
  /** Opens the Club Mexicano flow. */
  onOpenClubMexicano: () => void;
}

/** Mini-tournaments (Miniturneringer) format picker with icon filters; extracted verbatim from the monolith (legacy/App.jsx). */
function MiniTournamentsView({
  onBack,
  onOpenAmericano,
  onOpenTeamAmericano,
  onOpenMexicano,
  onOpenMixicano,
  onOpenTeamMexicano,
  onOpenMixAmericano,
  onOpenClubAmericano,
  onOpenClubTeamAmericano,
  onOpenClubMexicano,
}: MiniTournamentsViewProps) {
  // `tr`, not `t`: `t` is the icon type in the chip maps below.
  const tr = useT();
  // Icon types — describe the characteristic properties of each format.
  // A card can have several icons that are shown as chips in the header.
  // Type-to-rendering is handled by the IconChip helper below.
  //   'team'   — fixed partner pairs (Team formats)
  //   'random' — rotating partners (Americano-based)
  //   'skill'  — next round based on skill/standings (Mexicano-based)
  //   'mix'    — requires gender mixing (Mix Americano / Mix Mexicano)
  //   'club'   — between two clubs
  const miniTournaments = [
    {
      id: 'americano',
      name: 'Americano',
      tagline: tr('home.mini.americano.tagline'),
      desc: tr('home.mini.americano.desc'),
      icons: ['random'],
      // Yellow — classic Americano color
      theme: {
        gradient: 'linear-gradient(135deg, #fef9c3 0%, #fde047 60%, #facc15 100%)',
        text: '#422006',
        label: '#713f12',
      },
      features: [
        tr('home.mini.americano.f1'),
        tr('home.mini.americano.f2'),
        tr('home.mini.americano.f3'),
        tr('home.mini.americano.f4'),
      ],
      active: true,
      onClick: onOpenAmericano,
    },
    {
      id: 'mix_americano',
      name: 'Mix Americano',
      tagline: tr('home.mini.mixAmericano.tagline'),
      desc: tr('home.mini.mixAmericano.desc'),
      icons: ['random', 'mix'],
      // Orange — Americano with a pink undertone (mix accent)
      theme: {
        gradient: 'linear-gradient(135deg, #ffedd5 0%, #fdba74 60%, #fb923c 100%)',
        text: '#431407',
        label: '#9a3412',
      },
      features: [
        tr('home.mini.shared.oneWomanOneMan'),
        tr('home.mini.mixAmericano.f2'),
        tr('home.mini.shared.genderOverride'),
        tr('home.mini.mixAmericano.f4'),
      ],
      active: true,
      onClick: onOpenMixAmericano,
    },
    {
      id: 'mexicano',
      name: 'Mexicano',
      tagline: tr('home.mini.mexicano.tagline'),
      desc: tr('home.mini.mexicano.desc'),
      icons: ['skill'],
      // Darker orange — the Mexicano family
      theme: {
        gradient: 'linear-gradient(135deg, #fed7aa 0%, #fb923c 60%, #ea580c 100%)',
        text: '#431407',
        label: '#7c2d12',
      },
      features: [
        tr('home.mini.mexicano.f1'),
        tr('home.mini.mexicano.f2'),
        tr('home.mini.mexicano.f3'),
        tr('home.mini.mexicano.f4'),
      ],
      active: true,
      onClick: onOpenMexicano,
    },
    {
      id: 'mixicano',
      name: 'Mix Mexicano',
      tagline: tr('home.mini.mixicano.tagline'),
      desc: tr('home.mini.mixicano.desc'),
      icons: ['skill', 'mix'],
      // Deeper orange-pink
      theme: {
        gradient: 'linear-gradient(135deg, #fecaca 0%, #fb7185 60%, #e11d48 100%)',
        text: '#4c0519',
        label: '#9f1239',
      },
      features: [
        tr('home.mini.shared.oneWomanOneMan'),
        tr('home.mini.mixicano.f2'),
        tr('home.mini.shared.genderOverride'),
        tr('home.mini.mixicano.f4'),
      ],
      active: true,
      onClick: onOpenMixicano,
    },
    {
      id: 'team_americano',
      name: 'Team Americano',
      tagline: tr('home.mini.teamAmericano.tagline'),
      desc: tr('home.mini.teamAmericano.desc'),
      icons: ['random', 'team'],
      // Lime — the team family
      theme: {
        gradient: 'linear-gradient(135deg, #ecfccb 0%, #bef264 60%, #84cc16 100%)',
        text: '#1a2e05',
        label: '#365314',
      },
      features: [
        tr('home.mini.shared.fixedPairs'),
        tr('home.mini.teamAmericano.f2'),
        tr('home.mini.teamAmericano.f3'),
        tr('home.mini.teamAmericano.f4'),
      ],
      active: true,
      onClick: onOpenTeamAmericano,
    },
    {
      id: 'team_mexicano',
      name: 'Team Mexicano',
      tagline: tr('home.mini.teamMexicano.tagline'),
      desc: tr('home.mini.teamMexicano.desc'),
      icons: ['skill', 'team'],
      // Darker lime/green
      theme: {
        gradient: 'linear-gradient(135deg, #d9f99d 0%, #84cc16 60%, #4d7c0f 100%)',
        text: '#1a2e05',
        label: '#365314',
      },
      features: [
        tr('home.mini.shared.fixedPairs'),
        tr('home.mini.teamMexicano.f2'),
        tr('home.mini.teamMexicano.f3'),
        tr('home.mini.teamMexicano.f4'),
      ],
      active: true,
      onClick: onOpenTeamMexicano,
    },
    {
      id: 'club_americano',
      name: 'Club Americano',
      tagline: tr('home.mini.clubAmericano.tagline'),
      desc: tr('home.mini.clubAmericano.desc'),
      icons: ['random', 'club'],
      // Blue — the club family
      theme: {
        gradient: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 60%, #3b82f6 100%)',
        text: '#172554',
        label: '#1e3a8a',
      },
      features: [
        tr('home.mini.shared.twoVsTwo'),
        tr('home.mini.shared.clubLogoName'),
        tr('home.mini.shared.clubScoreIndividual'),
        tr('home.mini.clubAmericano.f4'),
      ],
      active: true,
      onClick: onOpenClubAmericano,
    },
    {
      id: 'club_team_americano',
      name: 'Club Team Americano',
      tagline: tr('home.mini.clubTeamAmericano.tagline'),
      desc: tr('home.mini.clubTeamAmericano.desc'),
      icons: ['random', 'team', 'club'],
      // Darker green — the club + team family
      theme: {
        gradient: 'linear-gradient(135deg, #d1fae5 0%, #6ee7b7 60%, #10b981 100%)',
        text: '#064e3b',
        label: '#065f46',
      },
      features: [
        tr('home.mini.clubTeamAmericano.f1'),
        tr('home.mini.clubTeamAmericano.f2'),
        tr('home.mini.shared.clubLogoName'),
        tr('home.mini.clubTeamAmericano.f4'),
      ],
      active: true,
      onClick: onOpenClubTeamAmericano,
    },
    {
      id: 'club_mexicano',
      name: 'Club Mexicano',
      tagline: tr('home.mini.clubMexicano.tagline'),
      desc: tr('home.mini.clubMexicano.desc'),
      icons: ['skill', 'club'],
      // Darker blue
      theme: {
        gradient: 'linear-gradient(135deg, #bfdbfe 0%, #60a5fa 60%, #1d4ed8 100%)',
        text: '#172554',
        label: '#1e40af',
      },
      features: [
        tr('home.mini.shared.twoVsTwo'),
        tr('home.mini.clubMexicano.f2'),
        tr('home.mini.clubMexicano.f3'),
        tr('home.mini.shared.clubScoreIndividual'),
      ],
      active: true,
      onClick: onOpenClubMexicano,
    },
  ];

  // IconChip — renders a single icon badge. Used as a visual shorthand for
  // what characterizes each format. The tooltip explains the meaning.
  // A shared white background makes the chips show up well on all card colors.
  const IconChip = ({ type, dark = false }) => {
    // Each icon has a logical foreground color (dark variant):
    //   team    — dark lime (fixed partner pairs)
    //   random  — dark orange (balanced rotation — everyone meets everyone)
    //   skill   — dark purple (level-based rotation — by standings)
    //   mix     — dark pink (gender mixing)
    //   club    — dark blue (club)
    const colors = {
      team: { fg: '#365314', label: tr('home.mini.icon.team') },
      random: { fg: '#a16207', label: tr('home.mini.icon.random') },
      skill: {
        fg: '#5b21b6',
        label: tr('home.mini.icon.skill'),
      },
      mix: { fg: '#9f1239', label: tr('home.mini.icon.mix') },
      club: { fg: '#1e40af', label: tr('home.mini.icon.club') },
    };
    const c = colors[type];
    if (!c) return null;

    // White background with a small shadow on all card colors, and a thin
    // border in the icon's color to define the edge against white too (legend).
    const baseClass =
      'inline-flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0 shadow-sm';
    const style = {
      background: '#ffffff',
      color: c.fg,
      border: `1px solid ${c.fg}33`, // 33 = ~20% opacity
      opacity: dark ? 0.65 : 1,
    };

    return (
      <span className={baseClass} style={style} title={c.label}>
        {type === 'team' && <Users size={13} strokeWidth={2.5} />}
        {type === 'random' && <Shuffle size={13} strokeWidth={2.5} />}
        {type === 'skill' && <BarChart3 size={13} strokeWidth={2.5} />}
        {type === 'mix' && (
          <span
            className="font-black leading-none tracking-tight"
            style={{ fontSize: '15px', letterSpacing: '-0.05em' }}
          >
            ♀♂
          </span>
        )}
        {type === 'club' && <Shield size={13} strokeWidth={2.75} />}
      </span>
    );
  };

  // FilterChip — clickable variant of IconChip that filters the tournaments.
  // The active state is shown with the icon's color as the background (inverted).
  const FilterChip = ({ type, label, active, onToggle }) => {
    const colors = {
      team: { fg: '#365314', label: tr('home.mini.icon.team') },
      random: { fg: '#a16207', label: tr('home.mini.icon.random') },
      skill: {
        fg: '#5b21b6',
        label: tr('home.mini.icon.skill'),
      },
      mix: { fg: '#9f1239', label: tr('home.mini.icon.mix') },
      club: { fg: '#1e40af', label: tr('home.mini.icon.club') },
    };
    const c = colors[type];
    if (!c) return null;

    return (
      <button
        type="button"
        onClick={onToggle}
        title={c.label}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all hover:opacity-90"
        style={{
          background: active ? c.fg : '#ffffff',
          color: active ? '#ffffff' : c.fg,
          border: `1px solid ${active ? c.fg : c.fg + '33'}`,
          boxShadow: active ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <span className="inline-flex items-center justify-center w-5 h-5">
          {type === 'team' && <Users size={12} strokeWidth={2.5} />}
          {type === 'random' && <Shuffle size={12} strokeWidth={2.5} />}
          {type === 'skill' && <BarChart3 size={12} strokeWidth={2.5} />}
          {type === 'mix' && (
            <span
              className="font-black leading-none tracking-tight"
              style={{ fontSize: '13px', letterSpacing: '-0.05em' }}
            >
              ♀♂
            </span>
          )}
          {type === 'club' && <Shield size={12} strokeWidth={2.75} />}
        </span>
        <span>{label}</span>
      </button>
    );
  };

  // Active filters: set of icon types. If empty: show everything. If not empty:
  // show only tournaments that have ALL selected icons ("AND" logic).
  const [activeFilters, setActiveFilters] = useState(new Set<string>());
  const toggleFilter = (type) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };
  const clearFilters = () => setActiveFilters(new Set());

  // Filter the tournaments based on active filters
  const filteredTournaments =
    activeFilters.size === 0
      ? miniTournaments
      : miniTournaments.filter((mt) => {
          // All active filters must be present in mt.icons
          for (const f of activeFilters) {
            if (!mt.icons?.includes(f)) return false;
          }
          return true;
        });

  // Two-column structure: the Americano family on the left, the Mexicano family on the right.
  // Within each column: plain → Team → Mix → Club → Club Team
  // The filter preserves this structure — only matching cards are shown, but column
  // membership is kept.
  const LEFT_COLUMN_IDS = [
    'americano',
    'team_americano',
    'mix_americano',
    'club_americano',
    'club_team_americano',
  ];
  const RIGHT_COLUMN_IDS = ['mexicano', 'team_mexicano', 'mixicano', 'club_mexicano'];

  const leftCol = filteredTournaments
    .filter((mt) => LEFT_COLUMN_IDS.includes(mt.id))
    .sort((a, b) => LEFT_COLUMN_IDS.indexOf(a.id) - LEFT_COLUMN_IDS.indexOf(b.id));
  const rightCol = filteredTournaments
    .filter((mt) => RIGHT_COLUMN_IDS.includes(mt.id))
    .sort((a, b) => RIGHT_COLUMN_IDS.indexOf(a.id) - RIGHT_COLUMN_IDS.indexOf(b.id));
  // Any formats that aren't in either column — shown
  // at the bottom as "other", independent of the column structure.
  const otherCol = filteredTournaments.filter(
    (mt) => !LEFT_COLUMN_IDS.includes(mt.id) && !RIGHT_COLUMN_IDS.includes(mt.id),
  );

  // Track which cards are expanded (showing the features list).
  // Compact by default — the user clicks the chevron to see more.
  const [expandedCards, setExpandedCards] = useState(new Set<string>());
  const toggleCardExpansion = (id) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Render helper for a single card. Extracted so the left/right column
  // use the same markup without duplication.
  const renderMiniCard = (mt) => {
    if (!mt) return null;
    // Default theme (fallback) if a format lacks an explicit theme
    const theme = mt.theme || {
      gradient: 'linear-gradient(135deg, #fef9c3 0%, #fde047 60%, #facc15 100%)',
      text: '#422006',
      label: '#713f12',
    };

    const isExpanded = expandedCards.has(mt.id);
    const hasFeatures = Array.isArray(mt.features) && mt.features.length > 0;

    if (mt.active && mt.onClick) {
      return (
        <div
          key={mt.id}
          className="card-elev overflow-hidden hover:shadow-lg transition-all"
          style={{ background: theme.gradient }}
        >
          <button onClick={mt.onClick} className="w-full text-left p-4">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <Play size={11} strokeWidth={2.5} style={{ color: theme.label }} />
                <span
                  className="font-mono text-[9px] font-bold tracking-[0.18em] uppercase"
                  style={{ color: theme.label }}
                >
                  {tr('home.mini.available')}
                </span>
              </div>
              {mt.icons && mt.icons.length > 0 && (
                <div className="flex items-center gap-1">
                  {mt.icons.map((t) => (
                    <IconChip key={t} type={t} />
                  ))}
                </div>
              )}
            </div>
            <h3
              className="font-display text-[15px] font-semibold mb-0.5 leading-tight"
              style={{ color: theme.text }}
            >
              {mt.name}
            </h3>
            <div
              className="font-mono text-[10px] uppercase tracking-wider"
              style={{ color: theme.label }}
            >
              {mt.tagline}
            </div>
          </button>
          {hasFeatures && (
            <button
              onClick={() => toggleCardExpansion(mt.id)}
              className="w-full px-4 py-1.5 flex items-center justify-between border-t text-[10px] font-mono uppercase tracking-wider hover:bg-black/5 transition-colors"
              style={{ borderTopColor: `${theme.label}30`, color: theme.label }}
              title={tr(isExpanded ? 'home.mini.hideDetails' : 'home.mini.showDetails')}
            >
              <span>{tr(isExpanded ? 'home.mini.less' : 'home.mini.moreInfo')}</span>
              {isExpanded ? (
                <ChevronUp size={11} strokeWidth={2.5} />
              ) : (
                <ChevronDown size={11} strokeWidth={2.5} />
              )}
            </button>
          )}
          {isExpanded && (
            <div className="px-4 pb-3 pt-1">
              <p className="text-[11px] leading-relaxed mb-2" style={{ color: theme.label }}>
                {mt.desc}
              </p>
              {hasFeatures && (
                <ul className="space-y-0.5">
                  {mt.features.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-1.5 text-[10px] leading-snug"
                      style={{ color: theme.label }}
                    >
                      <span className="flex-shrink-0 mt-0.5">·</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      );
    }
    // "Kommer snart" (coming soon) card — same compact style
    return (
      <div key={mt.id} className="card overflow-hidden opacity-60 cursor-not-allowed">
        <div className="p-4">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-2">
              <Clock size={11} strokeWidth={2.5} className="text-ink-3" />
              <span className="font-mono text-[9px] font-bold tracking-[0.18em] uppercase text-ink-3">
                {tr('home.mini.comingSoon')}
              </span>
            </div>
            {mt.icons && mt.icons.length > 0 && (
              <div className="flex items-center gap-1">
                {mt.icons.map((t) => (
                  <IconChip key={t} type={t} dark />
                ))}
              </div>
            )}
          </div>
          <h3 className="font-display text-[15px] font-semibold mb-0.5 leading-tight text-ink-2">
            {mt.name}
          </h3>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {mt.tagline}
          </div>
        </div>
        {hasFeatures && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleCardExpansion(mt.id);
            }}
            className="w-full px-4 py-1.5 flex items-center justify-between border-t border-line-soft text-[10px] font-mono uppercase tracking-wider text-ink-3 hover:bg-bg-soft transition-colors"
            style={{ pointerEvents: 'auto' }}
          >
            <span>{tr(isExpanded ? 'home.mini.less' : 'home.mini.moreInfo')}</span>
            {isExpanded ? (
              <ChevronUp size={11} strokeWidth={2.5} />
            ) : (
              <ChevronDown size={11} strokeWidth={2.5} />
            )}
          </button>
        )}
        {isExpanded && (
          <div className="px-4 pb-3 pt-1">
            <p className="text-[11px] leading-relaxed text-ink-3">{mt.desc}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 slide-up">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        >
          <ArrowLeft size={12} /> {tr('home.mini.back')}
        </button>
        <h1 className="font-display text-3xl font-semibold mb-1">{tr('home.mini.title')}</h1>
        <p className="text-sm text-ink-3 mb-2">{tr('home.mini.lead')}</p>
        <p className="text-xs text-ink-3 leading-relaxed">
          {tr('home.mini.eloWeightPre')} <strong>{tr('home.mini.eloWeightValue')}</strong>{' '}
          {tr('home.mini.eloWeightPost')}
        </p>
      </div>

      {/* Icon filter at the top — click to narrow down to only matching formats */}
      <div className="mb-5 card p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {tr('home.mini.filterByProperty')}
          </span>
          <div className="flex items-center gap-3">
            {(() => {
              // "Vis all info" (show all info) button that toggles based on whether all
              // visible cards are expanded or not. Visible cards = everything included in
              // filteredTournaments (both columns + others).
              const visibleIds = filteredTournaments.map((mt) => mt.id);
              const visibleExpandedCount = visibleIds.filter((id) => expandedCards.has(id)).length;
              const allExpanded =
                visibleIds.length > 0 && visibleExpandedCount === visibleIds.length;
              const toggleAll = () => {
                if (allExpanded) {
                  // Hide all visible
                  setExpandedCards((prev) => {
                    const next = new Set(prev);
                    visibleIds.forEach((id) => next.delete(id));
                    return next;
                  });
                } else {
                  // Show all visible
                  setExpandedCards((prev) => {
                    const next = new Set(prev);
                    visibleIds.forEach((id) => next.add(id));
                    return next;
                  });
                }
              };
              if (visibleIds.length === 0) return null;
              return (
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-1 text-[10px] font-mono text-ink-3 hover:text-ink-2 underline"
                  title={tr(
                    allExpanded ? 'home.mini.hideAllInfoTitle' : 'home.mini.showAllInfoTitle',
                  )}
                >
                  {allExpanded ? (
                    <ChevronUp size={11} strokeWidth={2.5} />
                  ) : (
                    <ChevronDown size={11} strokeWidth={2.5} />
                  )}
                  {tr(allExpanded ? 'home.mini.hideAllInfo' : 'home.mini.showAllInfo')}
                </button>
              );
            })()}
            {activeFilters.size > 0 && (
              <button
                onClick={clearFilters}
                className="text-[10px] font-mono text-ink-3 hover:text-ink-2 underline"
              >
                {tr('home.mini.reset', { count: String(activeFilters.size) })}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip
            type="random"
            label={tr('home.mini.filter.random')}
            active={activeFilters.has('random')}
            onToggle={() => toggleFilter('random')}
          />
          <FilterChip
            type="skill"
            label={tr('home.mini.filter.skill')}
            active={activeFilters.has('skill')}
            onToggle={() => toggleFilter('skill')}
          />
          <FilterChip
            type="team"
            label={tr('home.mini.filter.team')}
            active={activeFilters.has('team')}
            onToggle={() => toggleFilter('team')}
          />
          <FilterChip
            type="mix"
            label={tr('home.mini.filter.mix')}
            active={activeFilters.has('mix')}
            onToggle={() => toggleFilter('mix')}
          />
          <FilterChip
            type="club"
            label={tr('home.mini.filter.club')}
            active={activeFilters.has('club')}
            onToggle={() => toggleFilter('club')}
          />
        </div>
        {activeFilters.size > 0 && filteredTournaments.length === 0 && (
          <div className="mt-2 text-[11px] text-ink-3 italic">{tr('home.mini.noMatchCombo')}</div>
        )}
        {activeFilters.size > 0 && filteredTournaments.length > 0 && (
          <div className="mt-2 text-[10px] font-mono text-ink-3">
            {tr('home.mini.showingCount', {
              shown: String(filteredTournaments.length),
              total: String(miniTournaments.length),
            })}
          </div>
        )}
      </div>

      {/* Two-column grid where each row has the americano card on the left and
          the mexicano card on the right. Empty cells are rendered as placeholders so
          rows stay synchronized. items-stretch (the grid default) makes
          both cards in the same row equally tall. */}
      <div className="grid sm:grid-cols-2 gap-3 items-stretch">
        {(() => {
          // Build the row array: each row contains the left + right card
          const maxRows = Math.max(leftCol.length, rightCol.length);
          const cells = [];
          for (let i = 0; i < maxRows; i++) {
            const left = leftCol[i];
            const right = rightCol[i];
            if (left) cells.push(<div key={`l-${left.id}`}>{renderMiniCard(left)}</div>);
            else cells.push(<div key={`l-empty-${i}`} className="hidden sm:block" />);
            if (right) cells.push(<div key={`r-${right.id}`}>{renderMiniCard(right)}</div>);
            else cells.push(<div key={`r-empty-${i}`} className="hidden sm:block" />);
          }
          // If the filter empties the entire left or entire right column,
          // show a helpful message spanning across both columns.
          if (
            leftCol.length === 0 &&
            rightCol.length === 0 &&
            filteredTournaments.length === 0 &&
            otherCol.length === 0
          ) {
            return (
              <div className="sm:col-span-2 card p-4 text-center text-[11px] text-ink-3 italic">
                {tr('home.mini.noMatchFilters')}
              </div>
            );
          }
          return cells;
        })()}

        {/* Any "other" formats span across both columns */}
        {otherCol.length > 0 && (
          <div className="sm:col-span-2 space-y-3 pt-2 mt-2 border-t border-line-soft">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              {tr('home.mini.otherFormats')}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 items-stretch">
              {otherCol.map((mt) => (
                <div key={mt.id}>{renderMiniCard(mt)}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { MiniTournamentsView };
